import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {BehaviorSubject, Subject} from 'rxjs';
import {sortBy} from 'lodash-es';

import {getUuidFromIri} from '../../../shared/utilities/strings.utility';
import {SearchAddonDataService} from '../../services/search-addon-data.service';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {PageEvent} from '@angular/material/paginator';
import {PayableInvoice} from '../../models';
import * as moment from 'moment/moment';
import {extractIri} from '../../../shared/utilities/objects.utility';
import {ActivatedRoute, Router} from "@angular/router";
import {PayableInvoicesService} from "../../services";
import {LocalStorageService} from "../../../shared/services";
import {CustomersActions} from "../../../customers/store";
import {Store} from "@ngrx/store";
import {ApplicationState} from "../../../application-state/store";
import {PayableInvoicesActions} from "../../store";

@Component({
  selector: 'app-payable-invoices',
  styleUrls: ['payable-invoices-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="col pt-3">
      <div class="row">
        <div class="col">
          <app-payable-invoices-filter
            *ngIf="searchBoxVisible"
            [items]="(payableInvoices$ | async)"
            (updateFilter)="handleFilterFormAction($event)"
          ></app-payable-invoices-filter>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="table-paginator-group pos-relative">

            <app-loading-overlay *ngIf="loadingTable$|async"></app-loading-overlay>

            <app-payable-invoices-list
              *ngIf="(payableInvoices$ | async) as payableInvoices"
              [items]="payableInvoices"
            ></app-payable-invoices-list>

            <app-paginator-unstyled
              [totalItems]="payableInvoicesCount"
              [pageSize]="pageSize"
              [pageIndex]="currentPage - 1"
              [pageSizeOptions]="[5, 10, 15, 20, 25, 30]"
              (handleUpdatePageOrSize)="handleUpdatePageOrSize($event)"
              [showFirstLastButtons]="true"
            ></app-paginator-unstyled>
          </div>
        </div>
      </div>
    </div>
  `
})
export class PayableInvoicesViewComponent implements OnInit, OnDestroy {
  currentPage = 1;
  pageSize = 30;
  pageSizeOptions = [5, 10, 15, 20, 25];
  payableInvoices$: BehaviorSubject<Array<PayableInvoice>> = new BehaviorSubject<Array<PayableInvoice>>([]);
  loadingTable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  params$: BehaviorSubject<{ [key: string]: any }> = new BehaviorSubject<{ [p: string]: any }>({
    itemsPerPage: this.pageSize,
    page: this.currentPage,
    'order[createdAt]': 'desc',
  });

  activeFilter: { [p: string]: string } = {};
  searchBoxVisible = false;
  filterString = '';
  onDestroy$: Subject<any> = new Subject<any>();
  payableInvoicesCount: number;
  subscriptionList: any;

  constructor(
    private store$: Store<ApplicationState>,
    private searchAddonDataService: SearchAddonDataService,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private localStorageService: LocalStorageService,
    private payableInvoicesService: PayableInvoicesService
  ) {
    this.currentPage = 1;
  }

  ngOnInit(): void {
    this.loadPayableInvoices();
    const filters = this.localStorageService.getObjectByKey('filters', 'payable_invoices');
    if(filters) {
      this.handleFilterFormAction(filters);
    }

    this.searchAddonDataService
      .getObservable()
      .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(data => {
        this.filterString = data.filterString;
        this.searchBoxVisible = data.searchBoxVisible;
        this.activeFilter = data.activeFilter;
        this.cd.detectChanges();
      });

    this.route.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      this.currentPage = params['page'] ? +params['page'] : 1;
      this.updateParams();
    });

    this.searchAddonDataService
      .getObservable()
      .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(data => {
        this.handleSearchInput(data.filterString);
        this.cd.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  updateUrl() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: this.currentPage },
      queryParamsHandling: 'merge'
    });
  }

  handleUpdatePageOrSize(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.updateParams();
    this.loadPayableInvoices();
    this.updateUrl();
  }


  handleFilterFormAction(filters: any): void {
    const params = {
      state: null,
      department: null,
      orderNumber: null,
      'grossTotal.value[gte]': null,
      'grossTotal.value[lte]': null,
      'createdAt[strictly_after]': null,
      'createdAt[strictly_before]': null,
    };

    if (filters?.department) {
      params['issuer.uuid'] = getUuidFromIri(extractIri(filters.department));
    }
    if (filters?.orderNumber) {
      params.orderNumber =  String(filters.orderNumber);
    }

    if (filters?.state) {
      params.state = getUuidFromIri(extractIri(filters.state));
    }

    if (filters?.exactGrossPrice) {
      params['grossTotal.value[lte]'] = filters.maxGrossPrice;
    }

    if (filters?.minGrossPrice) {
      params['grossTotal.value[gte]'] = filters.minGrossPrice;
    }

    if (filters?.createdAtMin) {
      params['createdAt[strictly_after]'] = filters.createdAtMin;
    }

    if (filters?.createdAtMax) {
      params['createdAt[strictly_before]'] = filters.createdAtMax;
    }

    this.params$.next({
      ...this.params$.getValue(),
      ...params
    });
    this.updateParams();
    this.loadPayableInvoices();
  }

  loadPayableInvoices(): void {
    if (this.subscriptionList) {
      this.subscriptionList.unsubscribe();
    }
    this.loadingTable$.next(true);
    this.subscriptionList = this.payableInvoicesService.readPayableInvoices(this.currentPage, this.params$.getValue())
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(response => {
        this.loadingTable$.next(false);
        this.payableInvoicesCount = response['hydra:totalItems'];
        this.payableInvoices$.next(this.sortInvoices(response['hydra:member']));
      }, (fail) => {
        this.loadingTable$.next(false);
        console.error(fail);
      });
  }
  sortInvoices(toBeSortedInvoices: PayableInvoice[]): PayableInvoice[] {
    return sortBy(
      toBeSortedInvoices,
      e =>
        moment(e.bookedAt)
          .toDate()
          .getTime() * -1
    );
  }

  handleSearchInput(term: string): void {
    this.params$.next({
      ...this.params$.getValue(),
      fulltext_search: term ? term : ''
    });

    this.store$.dispatch(
      PayableInvoicesActions.ReadPayableInvoices({
        page: this.currentPage,
        params: this.params$.getValue()
      })
    );
    this.loadPayableInvoices();
  }

  private updateParams(): void {
    if (this.searchBoxVisible === false) {
      this.params$.next({
        ...this.params$.getValue(),
        'order[createdAt]': 'desc',
        fulltext_search: this.params$.getValue().hasOwnProperty('fulltext_search') ? this.params$.getValue().fulltext_search : ''
      });
    }
    this.params$.next({
      ...this.params$.getValue(),
      itemsPerPage: this.pageSize,
      page: this.currentPage
    });
  }
}
